import type { FollowType } from "@product/scmp-sdk";
import { FollowButtonDisplay, FollowButtonOutline, FollowButtonVariant } from "@product/scmp-sdk";
import { useCallback, useState } from "react";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as FollowButtonProps } from "~/components/entity-follow-button";
import { useUserFollow } from "~/components/follow-button/hooks";
import type { followButtonBase$key } from "~/queries/__generated__/followButtonBase.graphql";

import type { StyledMobileEntityFollowButtonProps } from "./styles";
import {
  Container,
  StyledDesktopEntityFollowButton,
  StyledMobileEntityFollowButton,
} from "./styles";

type Props = {
  buttonVariant?: StyledMobileEntityFollowButtonProps["$buttonVariant"];
  className?: string;
  reference: followButtonBase$key;
  type: FollowType;
} & Omit<FollowButtonProps, "reference">;

type ButtonStyleAttribute = {
  display: FollowButtonDisplay;
  outline: FollowButtonOutline;
};

type ButtonStyleProps = {
  desktop: ButtonStyleAttribute;
  mobile: ButtonStyleAttribute;
};

export const HomeFollowButton: FunctionComponent<Props> = ({
  buttonVariant,
  className,
  onClick,
  reference,
  type,
  ...props
}) => {
  const data = useFragment(
    graphql`
      fragment followButtonBase on Base {
        ...entityFollowButtonBase
        entityId
      }
    `,
    reference,
  );

  const { checkIsFollowed } = useUserFollow();

  const [isFollowButtonJustClicked, setIsFollowButtonJustClicked] = useState(false);
  const isFollowed = checkIsFollowed(type, data.entityId);

  const handleClick = useCallback(
    (isActionFollow: boolean) => {
      setIsFollowButtonJustClicked(true);
      onClick?.(isActionFollow);
    },
    [onClick],
  );

  const getButtonStyle = (): ButtonStyleProps => {
    switch (buttonVariant) {
      case "square":
        return {
          desktop: {
            display: FollowButtonDisplay.BlueSquareIcon,
            outline: FollowButtonOutline.Plain,
          },
          mobile: {
            display: FollowButtonDisplay.BlueSquareIcon,
            outline: FollowButtonOutline.Plain,
          },
        };

      default:
        return {
          desktop: {
            display: FollowButtonDisplay.BlueSquareIcon,
            outline: FollowButtonOutline.Plain,
          },
          mobile: {
            display: FollowButtonDisplay.BluePlusIcon,
            outline: FollowButtonOutline.Outlined,
          },
        };
    }
  };

  if (!isFollowButtonJustClicked && isFollowed) return null;

  const buttonStyle = getButtonStyle();
  return (
    <Container className={className}>
      <StyledMobileEntityFollowButton
        {...props}
        {...buttonStyle.mobile}
        $buttonVariant={buttonVariant}
        disabled={isFollowed}
        reference={data}
        variant={FollowButtonVariant.Homepage}
        isDisabledTooltip
        isLoadingEnabled
        onClick={handleClick}
      />
      <StyledDesktopEntityFollowButton
        {...props}
        {...buttonStyle.desktop}
        disabled={isFollowed}
        reference={data}
        variant={FollowButtonVariant.Homepage}
        isDisabledTooltip
        isLoadingEnabled
        onClick={handleClick}
      />
    </Container>
  );
};

HomeFollowButton.displayName = "HomeFollowButton";
